// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { downloadTextFile } from '@/lib/download';
import { useSessionCursorStack } from '@/lib/useSessionCursorStack';
import type { HealthAlertListItem } from '@greenlight/contract';
import { BulkActionToolbar } from './BulkActionToolbar';
import {
  healthAlertLabel,
  notifiedLabel,
  renderHealthAlertsMarkdown,
} from './healthAlertsMarkdown';

const PAGE_SIZE = 25;
const CURSOR_PARAM = 'hcursor';
const LIST_KEY = 'feedback-health-alerts';

const DELIVERY: Record<
  HealthAlertListItem['delivery']['status'],
  { variant: StatusBadgeVariant; label: string }
> = {
  not_sent: { variant: 'inactive', label: 'Not sent' },
  pending: { variant: 'pending', label: 'Sending' },
  sent: { variant: 'active', label: 'Sent to Shift' },
  gave_up: { variant: 'failed', label: 'Send failed' },
};

/** Health alerts view: every app that tripped into a crash loop, notified or not. */
export function HealthAlertsTab(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get(CURSOR_PARAM) ?? undefined;
  const trail = useSessionCursorStack(LIST_KEY);
  const clearTrail = trail.clear;
  React.useEffect(() => {
    if (!cursor) clearTrail();
  }, [cursor, clearTrail]);
  const [selected, setSelected] = React.useState<Map<string, HealthAlertListItem>>(new Map());
  const [sending, setSending] = React.useState(false);

  const key = [LIST_KEY, cursor ?? null];
  const query = api.feedback.healthAlerts.useQuery(
    key,
    { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
    { queryKey: key },
  );
  const items = query.data?.status === 200 ? query.data.body.items : [];
  const nextCursor = query.data?.status === 200 ? query.data.body.next_cursor : null;

  function toggleRow(item: HealthAlertListItem): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  }

  function setCursor(next: string | undefined): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set(CURSOR_PARAM, next);
        else params.delete(CURSOR_PARAM);
        return params;
      },
      { replace: true },
    );
  }

  function goNext(): void {
    if (!nextCursor) return;
    trail.push(cursor ?? '');
    setCursor(nextCursor);
  }

  function goPrev(): void {
    const prev = trail.pop();
    setCursor(prev === '' ? undefined : prev);
  }

  function downloadSelected(): void {
    const chosen = [...selected.values()];
    if (chosen.length === 0) return;
    downloadTextFile('greenlight-health-alerts.md', renderHealthAlertsMarkdown(chosen));
    toast.success(`Downloaded ${chosen.length} alert${chosen.length === 1 ? '' : 's'}.`);
    setSelected(new Map());
  }

  async function sendSelected(): Promise<void> {
    const ids = [...selected.keys()];
    if (ids.length === 0) return;
    setSending(true);
    try {
      const res = await api.feedback.shareHealthAlerts.mutation({ body: { ids } });
      if (res.status !== 200) throw new Error(`share returned ${res.status}`);
      const { queued, already_sent } = res.body;
      if (queued > 0) {
        toast.success(
          `Sending ${queued} alert${queued === 1 ? '' : 's'} to Shift.` +
            (already_sent > 0 ? ` ${already_sent} had already been sent.` : ''),
        );
      } else {
        toast.info('The selected alerts have already been sent to Shift.');
      }
      setSelected(new Map());
      void query.refetch();
    } catch {
      toast.error("Couldn't send the selected alerts. Retry in a moment.");
    } finally {
      setSending(false);
    }
  }

  const columns: DataListColumn<HealthAlertListItem>[] = [
    {
      key: 'app',
      header: 'App',
      primary: true,
      render: (i) => (
        <span className="block min-w-0">
          <span className="block truncate">{i.app_name}</span>
          <span className="block truncate font-mono text-[11px] text-muted-foreground">
            {i.app_slug}
          </span>
        </span>
      ),
    },
    {
      key: 'alert',
      header: 'Alert',
      render: (i) => <span className="text-[12px]">{healthAlertLabel(i)}</span>,
    },
    {
      key: 'notified',
      header: 'Notified',
      priority: 'md',
      render: (i) => <span className="text-[12px] text-muted-foreground">{notifiedLabel(i)}</span>,
    },
    {
      key: 'when',
      header: 'When',
      priority: 'md',
      render: (i) => (
        <span className="text-[12px] text-muted-foreground">
          {new Date(i.occurred_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'sent',
      header: 'Shift',
      render: (i) => (
        <StatusBadge variant={DELIVERY[i.delivery.status].variant}>
          {DELIVERY[i.delivery.status].label}
        </StatusBadge>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <BulkActionToolbar
        selectionCount={selected.size}
        actions={[
          {
            label: 'Download .md',
            onClick: downloadSelected,
            primary: true,
            testId: 'health-alerts-download',
          },
          {
            label: 'Send to Shift',
            onClick: () => void sendSelected(),
            busy: sending,
            busyLabel: 'Sending…',
            primary: true,
            testId: 'health-alerts-send-to-shift',
          },
        ]}
      />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&apos;t load health alerts. Retry in a moment.</AlertDescription>
        </Alert>
      ) : query.isLoading ? (
        <div className="space-y-2" data-testid="health-alerts-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <DataList
            items={items}
            columns={columns}
            rowKey={(i) => i.id}
            ariaLabel="Health alerts"
            selectedKeys={new Set(selected.keys())}
            onToggleRow={toggleRow}
            selectionLabel={(i) => `Select ${i.app_slug} alert at ${i.occurred_at}`}
            empty={
              <div className="rounded-gl border border-dashed border-border p-8 text-center">
                No health alerts yet. When an app starts crash-looping, the alert lands here.
              </div>
            }
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={!cursor}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!nextCursor}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
